// src/store/vendorsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient";

// ===== FETCH ALL =====
export const fetchVendors = createAsyncThunk(
  "vendors/fetchVendors",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get("/vendors/");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ===== INSERT (multipart/form-data) =====
// payload: { vendor, vendorDesc, status?, iconFile? }
export const insertVendor = createAsyncThunk(
  "vendors/insertVendor",
  async ({ vendor, vendorDesc, status = "A", iconFile }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("vendor", vendor);
      if (vendorDesc !== undefined && vendorDesc !== null) fd.append("vendorDesc", vendorDesc);
      fd.append("status", status);

      // IMPORTANT: backend expects File field name: "icon"
      if (iconFile) fd.append("icon", iconFile);

      const res = await apiClient.post("/vendors/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ===== UPDATE (multipart/form-data) =====
// payload: { id, vendor?, vendorDesc?, status?, iconFile? }
export const updateVendor = createAsyncThunk(
  "vendors/updateVendor",
  async ({ id, vendor, vendorDesc, status, iconFile }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      if (vendor !== undefined && vendor !== null) fd.append("vendor", vendor);
      if (vendorDesc !== undefined && vendorDesc !== null) fd.append("vendorDesc", vendorDesc);
      if (status !== undefined && status !== null) fd.append("status", status);

      // IMPORTANT: backend expects "icon"
      if (iconFile) fd.append("icon", iconFile);

      const res = await apiClient.put(`/vendors/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ===== PATCH STATUS =====
export const patchVendorStatus = createAsyncThunk(
  "vendors/patchVendorStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await apiClient.patch(`/vendors/${id}/status`, { status });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ===== DELETE =====
export const deleteVendor = createAsyncThunk(
  "vendors/deleteVendor",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiClient.delete(`/vendors/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const vendorsSlice = createSlice({
  name: "vendors",
  initialState: {
    vendors: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload || [];
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // insert
      .addCase(insertVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors.unshift(action.payload);
      })
      .addCase(insertVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update
      .addCase(updateVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVendor.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.vendors.findIndex((v) => v.id === action.payload.id);
        if (idx !== -1) state.vendors[idx] = action.payload;
      })
      .addCase(updateVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // patch status
      .addCase(patchVendorStatus.fulfilled, (state, action) => {
        const idx = state.vendors.findIndex((v) => v.id === action.payload.id);
        if (idx !== -1) state.vendors[idx] = action.payload;
      })
      .addCase(patchVendorStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // delete
      .addCase(deleteVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload?.id ?? action.meta.arg;
        state.vendors = state.vendors.filter((v) => v.id !== deletedId);
      })
      .addCase(deleteVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default vendorsSlice.reducer;
